import React, { useEffect, useRef } from 'react';
import './App.scss';
import Header from './Components/common-header/Header';
import Footer from './Components/common-footer/Footer';
import Hero from './Components/Hero/Hero';
import Calculator from './Components/Calculator/Calculator';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Section1 from './Components/Sections/Section1';
import Section2 from './Components/Sections/Section2';
import Section3 from './Components/Sections/Section3';
import Section4 from './Components/Sections/Section4';
import Section5 from './Components/Sections/Section5';
import Section6 from './Components/Sections/Section6';
import Section7 from './Components/Sections/Section7';
import inline1D from './assets/section2-desktop.png';
import inline1M from './assets/section2-mobile.png';
import InfoGraphic from './Components/InfoGraphic/InfoGraphic';
import inline2D from './assets/section5-desktop.png';
import inline2M from './assets/section5-mobile.png';
import VideoLoop from './Components/VideoLoop/VideoLoop';
import FactBox from './Components/FactBox/FactBox';
import CampaignFooter from './Components/CampaignFooter/CampaignFooter';
import ctaArrow from './assets/arrow.svg';

gsap.registerPlugin(ScrollTrigger);

function App() {
  const section1 = useRef();
  const section2 = useRef();
  const section3 = useRef();
  const inline1 = useRef();
  const inline2 = useRef();
  const headerRef = useRef();
  const ctaRef = useRef();

  useEffect(() => {
    ScrollTrigger.matchMedia({
      '(max-width: 767px)': function () {
        const sections = [section1, section2, section3];

        sections.forEach((panel, i) => {
          ScrollTrigger.create({
            trigger: panel.current,
            start: 'bottom bottom',
            pin: true,
            pinSpacing: false,
            scrub: 1
          });
        });
      }
    });
  }, []);

  useEffect(() => {
    const inlines = [inline1, inline2];
    inlines.forEach((panel, i) => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: panel.current,
          start: '10% bottom',
          end: 'top center',
          scrub: 1,
          once: true
        }
      });
      tl.to(panel.current, { width: '100%', duration: 3 });
    });
  }, []);

  useEffect(() => {
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: headerRef.current,
        start: 'bottom top',
        scrub: 1
      }
    });
    tl.to(headerRef.current, { y: -70, duration: 2 });

    const tlCta = gsap.timeline({
      scrollTrigger: {
        trigger: ctaRef.current,
        start: 'center 70%',
        end: 'center 70%',
        once: true
      }
    });
    tlCta.to('#pin', { opacity: 1, duration: 0.5 }).to('#pin', { y: 20, duration: 1.5 }, '-=0.5');
  }, []);

  return (
    <div className="app">
      <div className="app__contents">
        <div className="section" ref={section1}>
          <Header headerRef={headerRef} />
          <Hero />
          <Section1 />
        </div>

        <div className="section" ref={section2}>
          <div className="inline">
            <picture className="inline-photo" ref={inline1}>
              <source media="(min-width:461px)" srcSet={inline1D} />
              <source media="(max-width:460px)" srcSet={inline1M} />
              <img src={inline1D} alt="" />
            </picture>
          </div>
          <Section2 />

          <InfoGraphic />
          <Section3 />

          <Calculator />
          <Section4 />

          <VideoLoop />
          <Section5 />
        </div>

        <div className="section" ref={section3}>
          <div className="inline">
            <picture className="inline-photo" ref={inline2}>
              <source media="(min-width:461px)" srcSet={inline2D} />
              <source media="(max-width:460px)" srcSet={inline2M} />
              <img src={inline2D} alt="" />
            </picture>
          </div>
          <Section6 />

          <FactBox />
          <Section7 />
          <div className="cta" ref={ctaRef}>
            <div className="cta-contents">
              <svg
                id="Layer_1"
                data-name="Layer 1"
                xmlns="http://www.w3.org/2000/svg"
                className="cta-pin"
                viewBox="0 0 40.32 60"
              >
                <path
                  id="pin"
                  className="cls-1"
                  d="M20.16,31.94S9.68,19,6.45,9.26a14.52,14.52,0,1,1,27.42,0C30.65,19.84,20.16,31.94,20.16,31.94Zm0-33.07a5.65,5.65,0,1,0,5.65,5.65A5.64,5.64,0,0,0,20.16-1.13Z"
                  transform="translate(-0.12 10)"
                />
                <path
                  id="base"
                  className="cls-1"
                  d="M20.28,50C9.14,50,.12,46.75.12,42.74.12,39.64,5.55,37,13.17,36a58.8,58.8,0,0,0,7.11,8.39A59.6,59.6,0,0,0,27.39,36c7.62,1,13,3.68,13,6.78C40.44,46.75,31.41,50,20.28,50Z"
                  transform="translate(-0.12 10)"
                />
              </svg>
              <p className="cta-copy">
                Visit the{' '}
                <a
                  className="hyperLink mapLink"
                  href="https://sponsorcontent.cnn.com/interactive/nar/map/"
                  target="_blank"
                  rel="noreferrer"
                >
                  interactive map showing{' '}
                  <span className="italic">The State of Housing in America</span>
                </a>{' '}
                to see which areas have the greatest housing shortage and expect to see the most
                population growth among 173 metro areas across America.
              </p>
            </div>
          </div>
          <a
            href="https://www.realtor.com/thats-who-we-r/"
            target="_blank"
            rel="noreferrer"
            className="nar-href nar"
          >
            <div className="nar-href-button">
              Learn More <img src={ctaArrow} alt="arrow" className="nar-href-arrow" />
            </div>
          </a>
          <div className="divider--footer" />
          <div className="disclaimer">
            <sup>1</sup>
            <p className="disclaimer-copy">
              Tax liabilities differ according to the circumstances of each individual. Sellers
              should consult a qualified CPA, lawyer, or professional tax preparer to determine the
              tax liabilities generated by the sale of any property or other asset.
            </p>
          </div>

          <CampaignFooter />
          <Footer />
        </div>
      </div>
    </div>
  );
}

export default App;
